/**
 * Auth recovery for the Ato shell: when the account session ends while
 * something is still using it, ask the person to sign in again HERE, without
 * leaving the screen (or the framed App) they were on.
 *
 * Not a second session layer. The session is still Better Auth's cookie, read
 * by `fetchAccountSession`; the commit is still `useAccountController`'s
 * `applyAccountState`; cancellation is still `accountAuthEpoch`. This module
 * only owns the one question the rest of the app cannot answer on its own —
 * "is a challenge in progress, and how did it end?" — as a single flight.
 *
 * - One challenge at a time. Every caller (a 401 read, a framed App's request)
 *   joins the same Promise.
 * - A challenge is offered only when the API says the session EXPIRED. An
 *   explicit sign-out or a revocation ends recovery without one.
 * - A commit is fenced by the auth epoch captured when recovery started, so a
 *   sign-out racing a late session read can never sign the person back in.
 * - Completion elsewhere (another tab, the sign-in popup) arrives as a
 *   BroadcastChannel hint; polling the session covers browsers without it.
 */
import { withConnectionAuth } from "../app/connection/auth";
import { accountApiBase } from "../app/connection/resolveProfile";
import { accountAuthEpoch } from "./accountAuthEpoch";
import { fetchAccountSession } from "./accountSession";
import { isDevBypassEnabled } from "./devBypass";
import type { AccountAuthState, AccountSession } from "./accountTypes";

export type AuthRecoveryOutcome =
  | "recovered"
  | "signed_out"
  | "revoked"
  | "cancelled"
  | "unavailable";

export type AuthRecoveryView =
  | { kind: "idle" }
  /** The sign-in UI is shown; `attempt` remounts it for a new flight. */
  | { kind: "challenge"; attempt: number }
  /** Recovered; the sign-in UI may still be finishing (passkey enrollment). */
  | { kind: "finishing"; attempt: number };

type ServerAuthState = "signed_in" | "expired" | "signed_out" | "revoked";

export const AUTH_RECOVERY_CHANNEL = "ato-auth-recovery";
export const AUTH_RECOVERY_PROTOCOL = "ato.auth-recovery@1";
const POLL_MS_WITH_CHANNEL = 4000;
const POLL_MS_WITHOUT_CHANNEL = 1500;

interface Flight {
  promise: Promise<AuthRecoveryOutcome>;
  resolve: (outcome: AuthRecoveryOutcome) => void;
  epoch: number;
  attempt: number;
  stopWatching: () => void;
}

export interface AuthRecoveryDeps {
  fetchServerState: () => Promise<ServerAuthState | null>;
  fetchSession: () => Promise<AccountSession | null>;
  createChannel: () => BroadcastChannel | null;
}

async function fetchServerState(): Promise<ServerAuthState | null> {
  try {
    const response = await fetch(
      `${accountApiBase()}/v1/auth-recovery/state`,
      withConnectionAuth({ cache: "no-store", headers: { Accept: "application/json" } }),
    );
    if (!response.ok) return null;
    const body = (await response.json()) as { state?: unknown };
    return body.state === "signed_in" ||
      body.state === "expired" ||
      body.state === "signed_out" ||
      body.state === "revoked"
      ? body.state
      : null;
  } catch {
    return null;
  }
}

function createChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel !== "function") return null;
  try {
    return new BroadcastChannel(AUTH_RECOVERY_CHANNEL);
  } catch {
    return null;
  }
}

const defaultDeps: AuthRecoveryDeps = {
  fetchServerState,
  fetchSession: fetchAccountSession,
  createChannel,
};

let deps: AuthRecoveryDeps = defaultDeps;
let commit: ((state: AccountAuthState) => void) | null = null;
let knownSignedIn = false;
let flight: Flight | null = null;
let attempts = 0;
let view: AuthRecoveryView = { kind: "idle" };
const listeners = new Set<() => void>();

function setView(next: AuthRecoveryView): void {
  view = next;
  for (const listener of listeners) listener();
}

export function subscribeAuthRecovery(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthRecoveryView(): AuthRecoveryView {
  return view;
}

/**
 * The account lane registers its commit function, and tells recovery what it
 * last believed. A 401 on an ordinary API read only starts recovery for a
 * person who was signed in; a signed-out visitor is never challenged.
 */
export function registerAuthRecoveryAccount(
  apply: (state: AccountAuthState) => void,
): () => void {
  commit = apply;
  return () => {
    if (commit === apply) commit = null;
  };
}

export function noteAuthRecoveryAccountState(state: AccountAuthState): void {
  if (state.status === "signed_in") knownSignedIn = true;
  else if (state.status === "signed_out") knownSignedIn = false;
}

/** Tell other same-origin documents that a sign-in just committed. A hint only. */
export function announceSignedIn(): void {
  const channel = deps.createChannel();
  if (!channel) return;
  try {
    channel.postMessage({ type: "signed-in" });
  } catch {
    // Polling in the other document still converges.
  } finally {
    channel.close();
  }
}

function finish(
  current: Flight,
  outcome: AuthRecoveryOutcome,
  keepSignInOpen = false,
): void {
  if (flight !== current) return;
  current.stopWatching();
  flight = null;
  setView(
    keepSignInOpen && view.kind === "challenge"
      ? { kind: "finishing", attempt: current.attempt }
      : { kind: "idle" },
  );
  current.resolve(outcome);
}

/** Commit a session only if nothing (a sign-out) invalidated this flight. */
function commitIfCurrent(current: Flight, session: AccountSession): void {
  if (flight !== current) return;
  if (current.epoch !== accountAuthEpoch()) {
    finish(current, "signed_out");
    return;
  }
  commit?.({ status: "signed_in", session });
  finish(current, "recovered");
}

function watch(current: Flight): () => void {
  let stopped = false;
  let reading = false;
  const check = async () => {
    if (stopped || reading || flight !== current) return;
    reading = true;
    try {
      const session = await deps.fetchSession();
      if (session) commitIfCurrent(current, session);
      else if (current.epoch !== accountAuthEpoch()) finish(current, "signed_out");
    } catch {
      // A failed read is not an answer; the next tick asks again.
    } finally {
      reading = false;
    }
  };
  const channel = deps.createChannel();
  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown } | null;
      if (data?.type === "signed-in") void check();
    };
  }
  const timer = setInterval(
    () => void check(),
    channel ? POLL_MS_WITH_CHANNEL : POLL_MS_WITHOUT_CHANNEL,
  );
  const onVisible = () => {
    if (document.visibilityState === "visible") void check();
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    channel?.close();
  };
}

async function run(current: Flight): Promise<void> {
  const server = await deps.fetchServerState();
  if (flight !== current) return;
  if (current.epoch !== accountAuthEpoch()) return finish(current, "signed_out");
  if (server === "signed_out" || server === "revoked") return finish(current, server);
  if (server === null) return finish(current, "unavailable");
  if (server === "signed_in") {
    // Another document already recovered; adopt it without a challenge.
    try {
      const session = await deps.fetchSession();
      if (session) return commitIfCurrent(current, session);
    } catch {
      // Fall through: an unreadable session is not a recovered one.
    }
    return finish(current, "unavailable");
  }
  current.stopWatching = watch(current);
  setView({ kind: "challenge", attempt: current.attempt });
}

/**
 * Start (or join) recovery.
 *
 * `source: "api"` is an ordinary authenticated read that got a 401; it starts
 * recovery only for someone the shell believed was signed in. `source: "frame"`
 * is a framed App that was served to this person and then lost its session.
 */
export function requestAuthRecovery(
  source: "api" | "frame",
): Promise<AuthRecoveryOutcome> {
  if (flight) return flight.promise;
  if (isDevBypassEnabled()) return Promise.resolve("unavailable");
  if (source === "api" && !knownSignedIn) return Promise.resolve("unavailable");
  let resolve!: (outcome: AuthRecoveryOutcome) => void;
  const promise = new Promise<AuthRecoveryOutcome>((r) => {
    resolve = r;
  });
  attempts += 1;
  const current: Flight = {
    promise,
    resolve,
    epoch: accountAuthEpoch(),
    attempt: attempts,
    stopWatching: () => {},
  };
  flight = current;
  void run(current).catch(() => finish(current, "unavailable"));
  return promise;
}

/** The in-shell sign-in established a session. */
export function completeAuthRecovery(state: AccountAuthState): void {
  const current = flight;
  if (!current || state.status !== "signed_in") return;
  if (current.epoch !== accountAuthEpoch()) {
    finish(current, "signed_out");
    return;
  }
  commit?.(state);
  announceSignedIn();
  // The in-shell sign-in may still show passkey enrollment after an email code.
  finish(current, "recovered", true);
}

/** The person closed the challenge. */
export function cancelAuthRecovery(): void {
  if (flight) finish(flight, "cancelled");
  else if (view.kind !== "idle") setView({ kind: "idle" });
}

/** An explicit sign-out ends any challenge without committing anything. */
export function endAuthRecoveryForSignOut(): void {
  knownSignedIn = false;
  if (flight) finish(flight, "signed_out");
  else if (view.kind !== "idle") setView({ kind: "idle" });
}

/** The sign-in UI finished its own post-sign-in steps. */
export function dismissAuthRecovery(): void {
  if (!flight && view.kind === "finishing") setView({ kind: "idle" });
}

/**
 * Send an authenticated request; on a 401, join recovery and replay it once —
 * but only for GET/HEAD. A mutation is never replayed from here: this layer
 * cannot prove the server did not apply it.
 */
export async function fetchWithAuthRecovery(
  send: () => Promise<Response>,
  method: string,
  /** Pause request-scoped timers while the person signs in; the returned
   *  function resumes them just before the replay. */
  pause?: () => () => void,
): Promise<Response> {
  const response = await send();
  if (response.status !== 401) return response;
  const read = method.toUpperCase() === "GET" || method.toUpperCase() === "HEAD";
  if (!read) {
    void requestAuthRecovery("api");
    return response;
  }
  const resume = pause?.();
  const outcome = await requestAuthRecovery("api");
  resume?.();
  return outcome === "recovered" ? send() : response;
}

/** Test seam. */
export function resetAuthRecoveryForTests(next: Partial<AuthRecoveryDeps> = {}): void {
  flight?.stopWatching();
  flight = null;
  attempts = 0;
  commit = null;
  knownSignedIn = false;
  deps = { ...defaultDeps, ...next };
  view = { kind: "idle" };
  listeners.clear();
}
